import { isMultiPageTable } from "../utils/multiPageTableUtils";
import React, { memo, useMemo } from "react";

/**
 * Optimized grid lines rendering component
 * Memoizes grid line coordinates and SVG elements to prevent unnecessary recalculations
 */
const OptimizedGridLines = memo(
  ({
    gridLines,
    currentPage,
    canvasRef,
    pdfPageDimensions,
    dynamicGridLines,
    gridLineDragMode,
    drawingMode,
    isDraggingGridLine,
    draggingGridLine,
    isPlacingGridLine,
    pendingNewGridLines,
    handleGridLinePlacement,
    handlePdfClick,
    startGridLineDrag,
    // Deletion indicators
    selectedTable,
    deletedRows,
    deletedColumns,
    onMouseDown = null,
    onMouseMove = null,
    onMouseUp = null,
  }) => {
    // Memoize grid line calculations
    const gridLineElements = useMemo(() => {
      if (!gridLines || !canvasRef?.current || !pdfPageDimensions?.current) {
        return null;
      }

      const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;
      const pdfToCanvasScaleX = canvasRef.current.width / pdfWidth;
      const pdfToCanvasScaleY = canvasRef.current.height / pdfHeight;

      const isDynamic = Boolean(dynamicGridLines?.[currentPage]);
      const canInteract = isDynamic && gridLineDragMode && !drawingMode;

      const elements = [];

      // Horizontal lines
      gridLines.horizontal?.forEach((line, idx) => {
        const x1 = line.x0 * pdfToCanvasScaleX;
        const y1 = line.y0 * pdfToCanvasScaleY - (isDynamic ? 0 : 5);
        const x2 = line.x1 * pdfToCanvasScaleX;
        const y2 = line.y1 * pdfToCanvasScaleY - (isDynamic ? 0 : 5);

        const isBeingDragged =
          isDraggingGridLine &&
          draggingGridLine?.type === "horizontal" &&
          draggingGridLine?.index === idx;

        elements.push(
          <g key={`h-group-${idx}`}>
            {/* Invisible wider line for better interaction */}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={"transparent"}
              strokeWidth={"10"}
              style={{
                cursor: canInteract ? "ns-resize" : "default",
                pointerEvents: canInteract ? "stroke" : "none",
              }}
              onMouseDown={
                canInteract
                  ? (e) => {
                      const cellFound = handlePdfClick?.(e.nativeEvent);
                      if (!cellFound && startGridLineDrag) {
                        startGridLineDrag(e, "horizontal", idx, line);
                      }
                    }
                  : undefined
              }
            />
            {/* Visible line */}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={
                isBeingDragged ||
                (pendingNewGridLines &&
                  pendingNewGridLines.type === "horizontal" &&
                  pendingNewGridLines.index === idx &&
                  pendingNewGridLines.page === currentPage)
                  ? "#ff9800"
                  : isDynamic
                  ? "#4caf50"
                  : "#2196f3"
              }
              strokeWidth={
                isBeingDragged ||
                (pendingNewGridLines &&
                  pendingNewGridLines.type === "horizontal" &&
                  pendingNewGridLines.index === idx &&
                  pendingNewGridLines.page === currentPage)
                  ? 4
                  : isDynamic
                  ? 2
                  : 1.5
              }
              strokeOpacity={0.7}
              strokeDasharray={isDynamic ? "8,4" : "5,3"}
              style={{
                pointerEvents: "none",
                cursor: "default",
              }}
            />
          </g>
        );
      });

      // Vertical lines
      gridLines.vertical?.forEach((line, idx) => {
        const x1 = line.x0 * pdfToCanvasScaleX - (isDynamic ? 0 : 5);
        const y1 = line.y0 * pdfToCanvasScaleY;
        const x2 = line.x1 * pdfToCanvasScaleX - (isDynamic ? 0 : 5);
        const y2 = line.y1 * pdfToCanvasScaleY;

        const isBeingDragged =
          isDraggingGridLine &&
          draggingGridLine?.type === "vertical" &&
          draggingGridLine?.index === idx;

        elements.push(
          <g key={`v-group-${idx}`}>
            {/* Invisible wider line for better interaction */}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={"transparent"}
              strokeWidth={"10"}
              style={{
                cursor: canInteract ? "ew-resize" : "default",
                pointerEvents: canInteract ? "stroke" : "none",
              }}
              onMouseDown={
                canInteract
                  ? (e) => {
                      const cellFound = handlePdfClick?.(e.nativeEvent);
                      if (!cellFound && startGridLineDrag) {
                        startGridLineDrag(e, "vertical", idx, line);
                      }
                    }
                  : undefined
              }
            />
            {/* Visible line */}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={
                isBeingDragged ||
                (pendingNewGridLines &&
                  pendingNewGridLines.type === "vertical" &&
                  pendingNewGridLines.index === idx &&
                  pendingNewGridLines.page === currentPage)
                  ? "#ff9800"
                  : isDynamic
                  ? "#4caf50"
                  : "#2196f3"
              }
              strokeWidth={
                isBeingDragged ||
                (pendingNewGridLines &&
                  pendingNewGridLines.type === "vertical" &&
                  pendingNewGridLines.index === idx &&
                  pendingNewGridLines.page === currentPage)
                  ? 4
                  : isDynamic
                  ? 2
                  : 1.5
              }
              strokeOpacity={0.7}
              strokeDasharray={isDynamic ? "8,4" : "5,3"}
              style={{
                pointerEvents: "none",
                cursor: "default",
              }}
            />
          </g>
        );
      });

      return elements;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      gridLines,
      canvasRef?.current?.width,
      canvasRef?.current?.height,
      pdfPageDimensions?.current,
      currentPage,
      dynamicGridLines,
      gridLineDragMode,
      drawingMode,
      isDraggingGridLine,
      draggingGridLine,
      pendingNewGridLines,
      handlePdfClick,
      startGridLineDrag,
    ]);

    // Memoize deletion indicators
    const deletionIndicators = useMemo(() => {
      if (!deletedRows && !deletedColumns) {
        return [];
      }

      const indicators = [];
      const baseGridLines = selectedTable?.grid_lines?.[currentPage];

      if (
        !baseGridLines ||
        !canvasRef?.current ||
        !pdfPageDimensions?.current
      ) {
        return indicators;
      }

      // Calculate PDF to canvas scaling
      const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;
      const pdfToCanvasScaleX = canvasRef.current.width / pdfWidth;
      const pdfToCanvasScaleY = canvasRef.current.height / pdfHeight;

      // Deleted columns indicators (vertical stripes)
      if (deletedColumns?.size > 0 && baseGridLines.vertical) {
        deletedColumns.forEach((columnInfo, columnId) => {
          // Parse the original position from column ID
          const originalColIndex = (() => {
            const parts = columnId.split("_");
            if (parts.length >= 2 && parts[0] === "col") {
              const parsedIndex = parseInt(parts[1], 10);
              return isNaN(parsedIndex) ? -1 : parsedIndex;
            }
            return -1;
          })();

          // For visualization, use original column index with original grid lines
          // Column N is between grid lines N-1 and N in the original structure
          if (
            originalColIndex >= 0 &&
            originalColIndex < baseGridLines.vertical.length - 1
          ) {
            const leftLine = baseGridLines.vertical[originalColIndex - 1];
            const rightLine = baseGridLines.vertical[originalColIndex];

            const x = leftLine.x0 * pdfToCanvasScaleX;
            const width = (rightLine.x0 - leftLine.x0) * pdfToCanvasScaleX;
            const height = canvasRef.current.height;

            indicators.push(
              <g key={`deleted-col-${columnId}`}>
                {/* Red overlay with diagonal stripes */}
                <defs>
                  <pattern
                    id={`delete-stripe-col-${columnId}`}
                    patternUnits={"userSpaceOnUse"}
                    width={"8"}
                    height={"8"}
                    patternTransform={"rotate(45)"}
                  >
                    <rect
                      width={"8"}
                      height={"8"}
                      fill={"rgba(244, 67, 54, 0.1)"}
                    />
                    <rect
                      width={"4"}
                      height={"8"}
                      fill={"rgba(244, 67, 54, 0.3)"}
                    />
                  </pattern>
                </defs>
                <rect
                  x={x}
                  y={0}
                  width={width}
                  height={height}
                  fill={`url(#delete-stripe-col-${columnId})`}
                  stroke={"#f44336"}
                  strokeWidth={"2"}
                  strokeDasharray={"10,5"}
                  opacity={"0.8"}
                />
              </g>
            );
          }
        });
      }

      // Deleted rows indicators (horizontal stripes) - page-specific
      if (deletedRows?.size > 0 && baseGridLines.horizontal) {
        deletedRows.forEach((rowInfo, rowId) => {
          // Check if this deleted row belongs to the current page
          const rowPageInfo = rowInfo.pageInfo;
          if (!rowPageInfo || !rowPageInfo.includes(currentPage)) {
            return; // Skip this row if it doesn't belong to current page
          }

          // Parse the original position from row ID
          const globalRowIndex = (() => {
            const parts = rowId.split("_");
            if (parts.length >= 2 && parts[0] === "row") {
              const parsedIndex = parseInt(parts[1], 10);
              return isNaN(parsedIndex) ? -1 : parsedIndex;
            }
            return -1;
          })();

          // Convert global row index to page-relative grid index
          // Check if this is a multi-page table using existing utility
          const isMultiPage = isMultiPageTable(selectedTable);

          // For individual tables: use direct mapping (row 0 -> gridLine 0)
          // For combined tables: use direct mapping initially
          let gridLineIndex = globalRowIndex;

          // For multi-page tables, map global index to page-relative index
          if (selectedTable && globalRowIndex >= 0 && isMultiPage) {
            // Get ALL row indices that appear on this page (including deleted ones)
            // We need to reconstruct what the page looked like before deletions
            const allRowIndicesOnPage = new Set();

            // Add current rows still in selectedTable on this page
            Object.keys(selectedTable).forEach((cellKey) => {
              if (cellKey.match(/^\d+_\d+$/)) {
                const [cellRowStr] = cellKey.split("_");
                const cellRowIndex = parseInt(cellRowStr);
                const cellData = selectedTable[cellKey];

                if (
                  cellData &&
                  Array.isArray(cellData) &&
                  cellData[0] &&
                  cellData[0].page === currentPage
                ) {
                  allRowIndicesOnPage.add(cellRowIndex);
                }
              }
            });

            // Add deleted rows that belong to this page
            deletedRows.forEach((deletedRowInfo) => {
              if (
                deletedRowInfo.pageInfo &&
                deletedRowInfo.pageInfo.includes(currentPage)
              ) {
                // Convert 0-based originalIndex to 1-based to match cell key format
                allRowIndicesOnPage.add(deletedRowInfo.originalIndex + 1);
              }
            });

            // Sort all row indices to get original page order
            const sortedRowIndices = Array.from(allRowIndicesOnPage).sort(
              (a, b) => a - b
            );

            // Find the grid index for this global row index
            // Convert 0-based globalRowIndex to 1-based to match sortedRowIndices format
            const pageRelativeIndex = sortedRowIndices.indexOf(
              globalRowIndex + 1
            );
            if (pageRelativeIndex >= 0) {
              // For combined tables, pageRelativeIndex directly maps to gridLineIndex
              gridLineIndex = pageRelativeIndex;
            }
          }

          if (
            globalRowIndex >= 0 &&
            gridLineIndex < baseGridLines.horizontal.length
          ) {
            // Calculate header end grid line for row boundaries
            const headerEndGridLine =
              gridLineIndex > 0
                ? baseGridLines.horizontal[gridLineIndex - 1]
                : baseGridLines.vertical && baseGridLines.vertical.length > 0
                ? {
                    // For first row, use the top Y coordinate where vertical lines start
                    y0: Math.min(
                      ...baseGridLines.vertical.map((line) => line.y0)
                    ),
                    y1: Math.min(
                      ...baseGridLines.vertical.map((line) => line.y1)
                    ),
                  }
                : { y0: 0, y1: 0 };

            const topLine = headerEndGridLine;
            const bottomLine = baseGridLines.horizontal[gridLineIndex];

            // Use original coordinates without grid line offset (deletion overlays use raw coordinates)
            const y = topLine.y0 * pdfToCanvasScaleY;
            const height = (bottomLine.y0 - topLine.y0) * pdfToCanvasScaleY;
            const width = canvasRef.current.width;

            indicators.push(
              <g key={`deleted-row-${rowId}`}>
                {/* Red overlay with diagonal stripes */}
                <defs>
                  <pattern
                    id={`delete-stripe-row-${rowId}`}
                    patternUnits={"userSpaceOnUse"}
                    width={"8"}
                    height={"8"}
                    patternTransform={"rotate(-45)"}
                  >
                    <rect
                      width={"8"}
                      height={"8"}
                      fill={"rgba(244, 67, 54, 0.1)"}
                    />
                    <rect
                      width={"4"}
                      height={"8"}
                      fill={"rgba(244, 67, 54, 0.3)"}
                    />
                  </pattern>
                </defs>
                <rect
                  x={0}
                  y={y}
                  width={width}
                  height={height}
                  fill={`url(#delete-stripe-row-${rowId})`}
                  stroke={"#f44336"}
                  strokeWidth={"2"}
                  strokeDasharray={"10,5"}
                  opacity={"0.8"}
                  style={{ cursor: "default" }}
                />
              </g>
            );
          }
        });
      }

      return indicators;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      deletedRows,
      deletedColumns,
      selectedTable,
      currentPage,
      canvasRef?.current?.width,
      canvasRef?.current?.height,
      pdfPageDimensions?.current,
    ]);

    if (
      !gridLines ||
      !canvasRef?.current ||
      !pdfPageDimensions?.current ||
      !canvasRef.current.width ||
      !canvasRef.current.height
    ) {
      return null;
    }

    return (
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none", // Let events fall through to background SVG
          zIndex: 5,
        }}
        viewBox={`0 0 ${canvasRef.current.width} ${canvasRef.current.height}`}
        onClick={isPlacingGridLine ? handleGridLinePlacement : undefined}
        onMouseDown={onMouseDown || undefined}
        onMouseMove={onMouseMove || undefined}
        onMouseUp={onMouseUp || undefined}
      >
        {gridLineElements}
        {deletionIndicators}
      </svg>
    );
  }
);

OptimizedGridLines.displayName = "OptimizedGridLines";

export default OptimizedGridLines;
